import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import get from 'lodash/get';
import take from 'lodash/take';
import orderBy from 'lodash/orderBy';
import { MessageBar } from 'office-ui-fabric-react';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { DialogType } from 'office-ui-fabric-react/lib/Dialog';
import {
  fetchDimensionListOverview,
  fetchDimensionListActivity,
  deleteDimensionList,
} from '../../actions/dimensionList';
import { formatDateString } from '../../utils';
import DimensionListItems from '../../components/DimensionListItems';
import SubModuleHeader from '../../components/SubModuleHeader';
import SubModule from '../../components/SubModule';
import Module from '../../components/Module';
import ModuleHeader from '../../components/ModuleHeader/ModuleHeader';
import ModuleContent from '../../components/ModuleContent';
import SubModuleContent from '../../components/SubModuleContent';
import SectionHeader from '../../components/SectionHeader';
import FluentGrid from '../../components/FluentGrid';
import styles from './DimensionListOverview.style';
import DataItem from '../../components/DataItem';
import ModuleContentList from '../../components/ModuleContentList';
import DimensionListActivity from '../../components/DimensionListActivity';
import { deleteDimensionContent as dialogContent, dimensionListTypes } from '../../constants';
import DeleteDialog from '../../components/DeleteDialog';
import { showFlashMessage } from '../../actions/ui';
import TextButton from '../../components/TextButton';

const DimensionListOverview = () => {
  const dispatch = useDispatch();
  const { pathname, state: locationState } = useLocation();
  const history = useHistory();

  const [count, setCount] = useState(3);
  const [loading, setLoading] = useState(true);
  const { listId } = useParams();
  const { listType } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const dimensionList = useSelector(state => {
    const list = {
      associatedLists: [],
      ...get(state, `entities.dimensionLists.${listId}`, {
        metadata: {},
      }),
    };
    // Only show associated lists that are direct parents for now.
    list.associatedLists = list.associatedLists.filter(
      associatedList => associatedList.level === 0,
    );
    return list;
  });

  const activities = useSelector(state =>
    orderBy(
      Object.values(get(state, `entities.activity`, {})).filter(
        activity =>
          activity.params.listId === listId ||
          activity.resource === listId ||
          activity.newData.listId === listId,
      ),
      ['timestamp'],
      ['desc'],
    ),
  );

  useEffect(() => {
    if (locationState) {
      setIsDialogVisible(locationState.delete);
    } else {
      setIsDialogVisible(false);
    }
  }, [locationState]);

  useEffect(() => {
    if (pathname) {
      setIsDialogVisible(false);
    }
  }, [pathname]);

  useEffect(() => {
    dispatch(fetchDimensionListOverview(listId, { useCache: false }));
    setLoading(false);

    // little hack to allow services time to process the activity log item
    setTimeout(() => {
      dispatch(fetchDimensionListActivity(listId));
    }, 1500);
  }, [dispatch, listId]);

  const handleDismiss = () => {
    history.replace({ state: null });
  };

  const handleDialogSubmit = async () => {
    setIsSubmitting(true);
    const result = await dispatch(deleteDimensionList(listId));
    setIsSubmitting(false);
    if (result && !result.error) {
      if (result && !result.error) {
        history.push({ pathname: '/dimensionLists' });
        dispatch(showFlashMessage('List successfully deleted', { type: 'success' }));
      }
    }
  };

  return (
    <>
      <Module>
        <ModuleContent data-testid="description">
          <FluentGrid>
            <FluentGrid row className={styles.details}>
              <FluentGrid col sm={12} md={6}>
                <SectionHeader noBorder noSpacing>
                  Description
                </SectionHeader>
                {dimensionList.listDescription && (
                  <Text as="p" variant="small" className={styles.description}>
                    {dimensionList.listDescription}
                  </Text>
                )}
              </FluentGrid>
              <FluentGrid col sm={12} md={6} className={styles.keywords}>
                <SectionHeader noBorder noSpacing>
                  Attributes
                </SectionHeader>
                <Stack vertical wrap tokens={{ childrenGap: 8 }}>
                  {dimensionList.listType && (
                    <DataItem label="List Type" className={styles.infoBlock}>
                      {dimensionListTypes.find(e => e.key === dimensionList.listType)
                        ? dimensionListTypes.find(e => e.key === dimensionList.listType).text
                        : dimensionList.listType.charAt(0).toUpperCase() +
                          dimensionList.listType.slice(1)}
                    </DataItem>
                  )}
                  {dimensionList.metadata.createdTimeStamp && (
                    <DataItem label="Date Created" className={styles.infoBlock}>
                      {formatDateString(dimensionList.metadata.createdTimeStamp)}
                    </DataItem>
                  )}
                  {dimensionList.metadata.createdBy &&
                    dimensionList.metadata.createdBy.displayName && (
                      <DataItem label="Created By" className={styles.infoBlock}>
                        {dimensionList.metadata.createdBy.displayName}
                      </DataItem>
                    )}
                  {dimensionList.associatedContractDetails && (
                    <DataItem label="Associated Contracts" className={styles.infoBlock}>
                      {dimensionList.associatedContractDetails.length === 0
                        ? '0'
                        : dimensionList.associatedContractDetails.length}
                    </DataItem>
                  )}
                </Stack>
              </FluentGrid>
            </FluentGrid>
          </FluentGrid>

          {dimensionList.listItems && dimensionList.listItems.length === 0 && (
            <MessageBar isMultiline className={styles.noDimensionDetailItems}>
              <Text>No {listType} lists or items</Text>
              <br />
              <Text className={styles.associatedContracts}>
                No {listType} lists or items have been added to this dimension list.
              </Text>
            </MessageBar>
          )}

          {dimensionList.listItems && dimensionList.listItems.length > 0 && (
            <SubModule data-testid="dimension-list-items-trigger">
              <SubModuleHeader id="dimension-list-items-heading">List Items</SubModuleHeader>
              <SubModuleContent data-testid="dimension-list-items" tabIndex="0">
                <span data-is-focusable>
                  <DimensionListItems
                    shimmered
                    enableShimmer={loading}
                    items={dimensionList.listItems}
                    listType={listType}
                    className={styles.scrollablePane}
                  />
                </span>
              </SubModuleContent>
            </SubModule>
          )}
        </ModuleContent>
      </Module>
      {dimensionList.associatedLists && dimensionList.associatedLists.length > 0 && (
        <Module data-testid="references">
          <ModuleHeader>
            Referenced in {dimensionList.associatedLists ? dimensionList.associatedLists.length : 0}{' '}
            {dimensionList.associatedLists.length === 1 ? 'Other List' : 'Other Lists'}
          </ModuleHeader>
          <ModuleContentList>
            <DimensionListItems
              shimmered
              enableShimmer={loading}
              associatedLists={dimensionList.associatedLists}
              listType={listType}
              aria-label=""
              className={styles.scrollablePane}
            />
          </ModuleContentList>
        </Module>
      )}

      <Module data-testid="associated-contracts">
        <ModuleHeader>Associated Contracts</ModuleHeader>
        <ModuleContentList>
          {dimensionList.associatedContractDetails &&
            dimensionList.associatedContractDetails.length > 0 && (
              <DimensionListItems
                shimmered
                enableShimmer={loading}
                items={dimensionList.associatedContractDetails}
                isAssociatedContracts
                className={styles.scrollablePane}
              />
            )}
        </ModuleContentList>
        {dimensionList.associatedContractDetails &&
          dimensionList.associatedContractDetails.length === 0 && (
            <MessageBar isMultiline className={styles.noDimensionDetail}>
              <Text>No Associated Contracts</Text>
              <br />
              <Text className={styles.associatedContracts}>
                Associate a contract from the contract detail page
              </Text>
            </MessageBar>
          )}
      </Module>

      <Module data-testid="product-list-activity">
        {activities && activities.length > 0 && (
          <ModuleHeader
            extra={
              <Text aria-label="Expanded" role="alert" as="span" className={styles.activityShowing}>
                Showing{' '}
                {activities.length > (activities.length > count ? count - 3 : count) ? (
                  <span>{count}</span>
                ) : (
                  <span>{activities.length}</span>
                )}{' '}
                of <span>{activities.length}</span>
              </Text>
            }
            className={styles.contractActivity}
          >
            {listType.charAt(0).toUpperCase() + listType.slice(1)} List Activity
          </ModuleHeader>
        )}
        <ModuleContent className={styles.activityContent}>
          {activities && <DimensionListActivity items={take(activities, count)} />}
          {activities?.length > 3 && activities.length > count && (
            <div>
              <TextButton
                tabIndex="0"
                noPadding
                aria-label="Show More Activity"
                className={styles.showMoreActivityButton}
                onClick={() => setCount(count + 10)}
              >
                <Text as="p" variant="small" className={styles.showMoreActivity}>
                  Show More Activity
                </Text>
              </TextButton>
            </div>
          )}
          {activities.length > 3 && activities?.length <= count && (
            <div>
              <TextButton
                tabIndex="0"
                noPadding
                aria-label="Show Less Activity"
                className={styles.showMoreActivityButton}
                onClick={() => setCount(3)}
              >
                <Text as="p" variant="small" className={styles.showMoreActivity}>
                  Show Less Activity
                </Text>
              </TextButton>
            </div>
          )}
        </ModuleContent>
      </Module>
      <DeleteDialog
        onDismiss={handleDismiss}
        onSubmit={handleDialogSubmit}
        isRemoving={isSubmitting}
        hidden={!isDialogVisible}
        dialogContentProps={{
          type: DialogType.close,
          title: dialogContent.title,
          closeButtonAriaLabel: 'Close',
        }}
        dialogBodyContent={{
          content: `${dialogContent.content}`,
          buttonText: dialogContent.button,
          showCancelButton: dialogContent.showCancelButton,
        }}
      />
    </>
  );
};

export default DimensionListOverview;
